// ─────────────────────────────────────────────────────────────────────────────
// Frontend mirror of srv/shared/permissions.js. Must stay in sync — CRA
// can't import from outside src/, so we keep an ES6 copy here.
//
// 2026-05-17 — Phase C of the hierarchical-permissions restructure. The
// SCOPES tree below is the single source of truth for the Admin Feature
// Grid, Sidebar visibility, Header dropdown, and every route guard in App.js.
// To add a new sub-capability:
//   1. Add it under the matching scope's `caps` here (label + order).
//   2. Add it to srv/shared/permissions.js (backend mirror, same key).
//   3. Restart backend (so requireCapability gates accept the new name).
//
// IMPORTANT: cap keys are stable identifiers persisted in users.access.
// Don't rename them without a DB migration.
// ─────────────────────────────────────────────────────────────────────────────

// 2026-10-27 — owner-requested Feature Grid restructure. TV Manager keeps
// only its 3 device-level caps (Players/Files/Groups); Info Screens, F&B Menu
// and Live Stream now render together as a hand-built "Screen Manager" card
// in AdminPanel.js instead of a generic SCOPES-driven one — see the comment
// there. They are intentionally NOT listed here even though their data still
// lives under access.tv.caps.info_screen / access.menu.enabled /
// access.live_stream.enabled: this SCOPES export only drives the generic
// Feature Grid loop now, not every cap AdminPanel can read or write.
// menu/pos/kitchen were dropped from THIS catalog (so they stop rendering as
// cards) but are still declared in the backend mirror (srv/shared/
// permissions.js) so existing grants on those keys keep round-tripping
// through sanitizeAccess instead of being silently wiped on next save.
export const SCOPES = {
  tv: {
    label: 'TV Manager',
    icon: '📺',
    order: 1,
    caps: {
      players: { label: 'Players', order: 1 },
      files:   { label: 'Files',   order: 2 },
      groups:  { label: 'Groups',  order: 3 },
    },
  },
};

// Build a default empty `access` object with all scopes/caps disabled.
export function emptyAccess() {
  const out = {};
  for (const [scopeKey, scope] of Object.entries(SCOPES)) {
    const caps = {};
    for (const capKey of Object.keys(scope.caps)) {
      caps[capKey] = { enabled: false, expires_at: null };
    }
    out[scopeKey] = { enabled: false, expires_at: null, caps };
  }
  return out;
}

export function isAdminUser(user) {
  return !!(user && (user.is_admin === true || user.user_type === 'admin' || user.role === 'admin'));
}

export function isExpired(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

export function effectiveCapExpiry(access, scopeKey, capKey) {
  const scope = access && access[scopeKey];
  if (!scope) return null;
  const cap = scope.caps && scope.caps[capKey];
  if (!cap) return null;
  return cap.expires_at || scope.expires_at || null;
}

export function canAccessScope(user, scopeKey) {
  if (isAdminUser(user)) return true;
  if (!user || !user.access) return false;
  const scope = user.access[scopeKey];
  if (!scope || scope.enabled !== true) return false;
  if (isExpired(scope.expires_at)) return false;
  return true;
}

export function canAccessCap(user, scopeKey, capKey) {
  if (isAdminUser(user)) return true;
  if (!canAccessScope(user, scopeKey)) return false;
  const scope = user.access[scopeKey];
  const cap = scope.caps && scope.caps[capKey];
  if (!cap || cap.enabled !== true) return false;
  if (isExpired(effectiveCapExpiry(user.access, scopeKey, capKey))) return false;
  return true;
}

// Convenience: does the user have ANY cap inside a scope? Used by Sidebar
// to decide whether to show the group header at all.
export function hasAnyCapInScope(user, scopeKey) {
  if (isAdminUser(user)) return true;
  const scope = user?.access?.[scopeKey];
  if (!scope || scope.enabled !== true) return false;
  for (const capKey of Object.keys(scope.caps || {})) {
    if (canAccessCap(user, scopeKey, capKey)) return true;
  }
  return false;
}

// Order-preserving iterator: sorted [scopeKey, scopeDef] pairs.
export function scopesByOrder() {
  return Object.entries(SCOPES).sort((a, b) => (a[1].order || 99) - (b[1].order || 99));
}

// Order-preserving iterator for caps inside a scope.
export function capsByOrder(scopeKey) {
  const scope = SCOPES[scopeKey];
  if (!scope) return [];
  return Object.entries(scope.caps || {}).sort((a, b) => (a[1].order || 99) - (b[1].order || 99));
}
